import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Eye, Headphones, TicketCheck, Clock, CheckCircle2, AlertTriangle,
  ChevronLeft, ChevronRight, Loader2, Filter, SlidersHorizontal, RotateCcw,
} from 'lucide-react';
import { adminAPI } from '../../../services/api';
import { toast } from 'react-toastify';
import ActionMenu from '../../shared/ActionMenu';

/* ── design tokens ── */
const C = {
  bg: '#f4f4f9', surface: '#FFFFFF', border: '#e8e8f1', borderSoft: '#f0f0f6',
  ink: '#14142b', body: '#4a4a6a', muted: '#6e6e86',
  primary: '#7c3aed', primarySoft: '#f1ebff', primaryTint: '#ddd6fe',
  success: '#10B981', successSoft: '#D1FAE5',
  warning: '#F59E0B', warnSoft: '#FEF3C7',
  danger: '#EF4444', dangerSoft: '#FEE2E2',
  radius: 14, radiusSm: 8,
};

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const StatusBadge = ({ status }) => {
  const map = {
    open:        { bg: C.primarySoft, color: C.primary,  label: 'Open'        },
    in_progress: { bg: C.warnSoft,    color: C.warning,  label: 'In Progress' },
    resolved:    { bg: C.successSoft, color: C.success,  label: 'Resolved'    },
    closed:      { bg: '#F1F5F9',     color: C.muted,    label: 'Closed'      },
  };
  const s = map[status] || { bg: '#F1F5F9', color: C.muted, label: status };
  return <span style={{ padding: '3px 10px', borderRadius: 999, background: s.bg, color: s.color, fontSize: 11, fontWeight: 700, textTransform: 'capitalize', letterSpacing: .3 }}>{s.label}</span>;
};

const PriorityBadge = ({ priority }) => {
  const map = {
    low:    { bg: '#F1F5F9',     color: C.muted,   label: 'Low'    },
    medium: { bg: C.warnSoft,    color: C.warning, label: 'Medium' },
    high:   { bg: C.dangerSoft,  color: C.danger,  label: 'High'   },
    urgent: { bg: C.dangerSoft,  color: C.danger,  label: 'Urgent' },
  };
  const s = map[priority] || { bg: '#F1F5F9', color: C.muted, label: priority };
  return (
    <span style={{ padding: '3px 10px', borderRadius: 999, background: s.bg, color: s.color, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .3, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      {(priority === 'high' || priority === 'urgent') && <AlertTriangle size={10} strokeWidth={2.5} />}
      {s.label}
    </span>
  );
};

const KpiCard = ({ label, value, sub, color, bg, Icon, active, onClick }) => {
  const [hov, setHov] = useState(false);
  return (
    <div onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)} style={{
      background: C.surface, borderRadius: C.radius,
      border: `1.5px solid ${active ? color : hov ? C.primaryTint : C.border}`,
      padding: '18px 20px', cursor: onClick ? 'pointer' : 'default',
      boxShadow: active ? `0 4px 20px ${color}20` : '0 1px 4px rgba(20,20,43,0.04)',
      transition: 'all .18s', display: 'flex', flexDirection: 'column', gap: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 6 }}>{label}</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: active ? color : C.ink, lineHeight: 1, letterSpacing: '-.02em', minHeight: 30, display: 'flex', alignItems: 'center' }}>{value}</div>
        </div>
        <div style={{ width: 40, height: 40, borderRadius: 10, background: active ? color : bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'background .18s' }}>
          <Icon size={18} color={active ? '#fff' : color} strokeWidth={2} />
        </div>
      </div>
      {sub && <div style={{ fontSize: 12, color: active ? color : C.muted, fontWeight: 500 }}>{sub}</div>}
    </div>
  );
};

/* ══════════════════════════════════════════════════ */
const AdminSupport = () => {
  const navigate = useNavigate();
  const [loading,     setLoading]     = useState(true);
  const [tickets,     setTickets]     = useState([]);
  const [pagination,  setPagination]  = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    search: '', status: 'open', priority: 'all',
    page: 1, per_page: 15, sort_by: 'created_at', sort_order: 'desc',
  });
  const [stats, setStats] = useState({ total: 0, open: 0, inProgress: 0, resolved: 0 });

  useEffect(() => { fetchTickets(); }, [filters.page]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const r = await adminAPI.tickets.getAll(filters);
      const list = r.data.data.tickets;
      setTickets(list);
      setPagination(r.data.data.pagination);
      setStats({
        total:      list.length,
        open:       list.filter(t => t.status === 'open').length,
        inProgress: list.filter(t => t.status === 'in_progress').length,
        resolved:   list.filter(t => t.status === 'resolved').length,
      });
    } catch { toast.error('Failed to fetch tickets'); }
    finally { setLoading(false); }
  };

  const setFilter = (k, v) => setFilters(p => ({ ...p, [k]: v, page: 1 }));
  const setPage   = (p)    => setFilters(f => ({ ...f, page: p }));

  const pages = (() => {
    if (!pagination) return [];
    const { last_page: t, current_page: c } = pagination;
    let s = Math.max(1, c - 2), e = Math.min(t, s + 4);
    if (e - s < 4) s = Math.max(1, e - 4);
    return Array.from({ length: e - s + 1 }, (_, i) => s + i);
  })();

  const inp = { height: 38, borderRadius: C.radiusSm, border: `1.5px solid ${C.border}`, padding: '0 12px', fontSize: 13, color: C.ink, background: C.surface, outline: 'none', fontFamily: 'inherit' };

  const qTabs = [
    { label: 'Open',        val: 'open',        count: stats.open,       color: C.primary },
    { label: 'In Progress', val: 'in_progress', count: stats.inProgress, color: C.warning },
    { label: 'Resolved',    val: 'resolved',    count: stats.resolved,   color: C.success },
    { label: 'All',         val: 'all',         count: stats.total,      color: C.muted   },
  ];

  return (
    <div style={{ maxWidth: 1280, margin: '0 auto' }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg) } }
        .sup-kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 20px; }
        @media (max-width: 1100px) { .sup-kpi-grid { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 640px)  { .sup-kpi-grid { grid-template-columns: 1fr 1fr; } }
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 22 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: C.ink, margin: 0, letterSpacing: '-.02em' }}>Support Tickets</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: C.muted }}>Review and respond to user support requests.</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setShowFilters(v => !v)}
            style={{ height: 38, padding: '0 14px', borderRadius: C.radiusSm, background: showFilters ? C.primarySoft : C.surface, color: showFilters ? C.primary : C.body, border: `1.5px solid ${showFilters ? C.primaryTint : C.border}`, fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7, fontFamily: 'inherit' }}>
            <SlidersHorizontal size={14} /> Filters {showFilters ? '▴' : '▾'}
          </button>
          <button onClick={() => setFilters({ search: '', status: 'open', priority: 'all', page: 1, per_page: 15, sort_by: 'created_at', sort_order: 'desc' })}
            style={{ height: 38, padding: '0 14px', borderRadius: C.radiusSm, background: C.surface, color: C.body, border: `1.5px solid ${C.border}`, fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7, fontFamily: 'inherit' }}>
            <RotateCcw size={13} /> Reset
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="sup-kpi-grid">
        <KpiCard label="Open Tickets"  value={stats.open}       sub="Needs response"   color={C.primary} bg={C.primarySoft} Icon={TicketCheck}  active={filters.status === 'open'}        onClick={() => setFilter('status', filters.status === 'open' ? 'all' : 'open')} />
        <KpiCard label="In Progress"   value={stats.inProgress} sub="Being handled"    color={C.warning} bg={C.warnSoft}    Icon={Clock}         active={filters.status === 'in_progress'} onClick={() => setFilter('status', filters.status === 'in_progress' ? 'all' : 'in_progress')} />
        <KpiCard label="Resolved"      value={stats.resolved}   sub="Completed"        color={C.success} bg={C.successSoft} Icon={CheckCircle2}  active={filters.status === 'resolved'}    onClick={() => setFilter('status', filters.status === 'resolved' ? 'all' : 'resolved')} />
        <KpiCard label="Total Tickets" value={stats.total}      sub="All statuses"     color={C.muted}   bg='#F1F5F9'       Icon={Headphones}    active={filters.status === 'all'}         onClick={() => setFilter('status', 'all')} />
      </div>

      {/* Filter Bar */}
      <div style={{ background: C.surface, borderRadius: C.radius, border: `1.5px solid ${C.border}`, marginBottom: 16, overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', borderBottom: showFilters ? `1px solid ${C.borderSoft}` : 'none' }}>
          <div style={{ position: 'relative', flex: 1, borderRight: `1px solid ${C.borderSoft}` }}>
            <Search size={14} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: C.muted, pointerEvents: 'none' }} />
            <input style={{ ...inp, width: '100%', border: 'none', height: 46, paddingLeft: 38, borderRadius: 0, background: 'transparent' }}
              placeholder="Search by ticket ID, subject, or user…" value={filters.search}
              onChange={e => setFilter('search', e.target.value)} onKeyDown={e => e.key === 'Enter' && fetchTickets()} />
          </div>
          <div style={{ display: 'flex', padding: '0 12px', gap: 4, overflowX: 'auto', borderRight: `1px solid ${C.borderSoft}` }}>
            {qTabs.map(({ label, val, count, color }) => (
              <button key={val} onClick={() => setFilter('status', val)}
                style={{ height: 30, padding: '0 12px', borderRadius: 999, border: `1.5px solid ${filters.status === val ? color : 'transparent'}`, background: filters.status === val ? color : 'transparent', color: filters.status === val ? '#fff' : C.muted, fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 5, transition: 'all .15s' }}>
                {label}
                <span style={{ background: filters.status === val ? 'rgba(255,255,255,0.25)' : C.borderSoft, color: filters.status === val ? '#fff' : C.muted, fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 999 }}>{count}</span>
              </button>
            ))}
          </div>
          <div style={{ padding: '0 12px' }}>
            <button onClick={fetchTickets} style={{ height: 36, padding: '0 18px', borderRadius: C.radiusSm, background: C.primary, color: '#fff', border: 'none', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 7 }}>
              <Filter size={13} /> Apply
            </button>
          </div>
        </div>
        {showFilters && (
          <div style={{ padding: '14px 16px', background: C.bg, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <select style={{ ...inp, flex: '1 1 160px', cursor: 'pointer' }} value={filters.priority} onChange={e => setFilter('priority', e.target.value)}>
              <option value="all">All Priority</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
            <select style={{ ...inp, flex: '1 1 160px', cursor: 'pointer' }} value={filters.sort_order} onChange={e => setFilter('sort_order', e.target.value)}>
              <option value="desc">↓ Newest First</option>
              <option value="asc">↑ Oldest First</option>
            </select>
          </div>
        )}
      </div>

      {/* Table */}
      <div style={{ background: C.surface, borderRadius: C.radius, border: `1.5px solid ${C.border}`, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 60, textAlign: 'center', color: C.muted }}>
            <Loader2 size={28} style={{ animation: 'spin .8s linear infinite' }} />
            <p style={{ marginTop: 12, fontSize: 13 }}>Loading…</p>
          </div>
        ) : tickets.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: C.muted }}>
            <Headphones size={36} style={{ opacity: .3, marginBottom: 12 }} />
            <p style={{ fontSize: 13 }}>No support tickets found.</p>
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: C.bg, borderBottom: `1px solid ${C.border}` }}>
                    {['Ticket ID', 'User', 'Subject', 'Priority', 'Status', 'Created', 'Updated', 'Action'].map(h => (
                      <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: .5, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tickets.map((t, i) => (
                    <tr key={t.id} style={{
                      borderBottom: `1px solid ${C.borderSoft}`,
                      background: t.priority === 'urgent' ? '#FFF7F7' : i % 2 === 0 ? C.surface : C.bg,
                    }}>
                      <td style={{ padding: '12px 16px' }}>
                        <code style={{ fontSize: 12, fontFamily: 'Menlo,Consolas,monospace', color: C.primary, fontWeight: 700 }}>#{t.ticket_id}</code>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontWeight: 600, color: C.ink }}>{t.user?.username}</div>
                        <div style={{ fontSize: 11, color: C.muted }}>{t.user?.email}</div>
                      </td>
                      <td style={{ padding: '12px 16px', maxWidth: 280 }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: C.ink }}>{t.subject}</div>
                      </td>
                      <td style={{ padding: '12px 16px' }}><PriorityBadge priority={t.priority} /></td>
                      <td style={{ padding: '12px 16px' }}><StatusBadge status={t.status} /></td>
                      <td style={{ padding: '12px 16px', fontSize: 12, color: C.muted, whiteSpace: 'nowrap' }}>{fmtDate(t.created_at)}</td>
                      <td style={{ padding: '12px 16px', fontSize: 12, color: C.muted, whiteSpace: 'nowrap' }}>{fmtDate(t.updated_at)}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <ActionMenu items={[
                          { label: 'View & Reply', Icon: Eye, color: C.primary, bg: C.primarySoft, onClick: () => navigate(`/admin/support/${t.ticket_id}`) },
                        ]} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination && pagination.last_page > 1 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderTop: `1px solid ${C.borderSoft}` }}>
                <span style={{ fontSize: 12, color: C.muted }}>Showing {pagination.from}–{pagination.to} of {pagination.total} tickets</span>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button onClick={() => setPage(pagination.current_page - 1)} disabled={pagination.current_page === 1}
                    style={{ width: 30, height: 30, borderRadius: 6, border: `1px solid ${C.border}`, background: C.surface, cursor: 'pointer', color: C.body, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ChevronLeft size={14} />
                  </button>
                  {pages.map(p => (
                    <button key={p} onClick={() => setPage(p)} style={{
                      width: 30, height: 30, borderRadius: 6,
                      border: `1px solid ${p === pagination.current_page ? C.primary : C.border}`,
                      background: p === pagination.current_page ? C.primary : C.surface,
                      color: p === pagination.current_page ? '#fff' : C.body,
                      fontWeight: p === pagination.current_page ? 700 : 400, fontSize: 12, cursor: 'pointer',
                    }}>{p}</button>
                  ))}
                  <button onClick={() => setPage(pagination.current_page + 1)} disabled={pagination.current_page === pagination.last_page}
                    style={{ width: 30, height: 30, borderRadius: 6, border: `1px solid ${C.border}`, background: C.surface, cursor: 'pointer', color: C.body, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminSupport;
